import React, { useState } from 'react';
import { Table, Button } from 'reactstrap';
import { toast } from 'react-toastify';
import DownloadOutlineIcon from 'mdi-react/DownloadOutlineIcon';
import EditOutlineIcon from 'mdi-react/EditOutlineIcon';
import ContentCopyIcon from 'mdi-react/ContentCopyIcon';
import DeleteOutlineIcon from 'mdi-react/DeleteOutlineIcon';
import Api from '../../../../util/api';
import { partial } from '../../../../util/translation/translation';
import ShareModal from '../../../../shared/components/custom/ShareModal';
import AddCategoryForm from './AddCategoryForm';
import FormCard from '../../../../shared/components/custom/FormCard';
import SquareIconButton from '../../../../shared/components/custom/SquareIconButton';
import DeleteModal from '../../../../shared/components/custom/DeleteModal';
import useModal from '../../../../shared/hooks/useModal';
import skeletonTableLoader from '../../../../shared/components/custom/SkeletonLoaders';

const EventCategoryCard = ({
  categories,
  loadingCategories,
  fetchCategories,
  setLoading,
  exportEventCategoriesForAllCompanies,
}) => {
  const p = partial('EventCategories');
  const s = partial('shared');
  const ep = partial('EventsPage');
  const [handleModal, visible, deleteId] = useModal();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isEditConfig, setIsEditConfig] = useState(false);
  const [isCloneConfig, setIsCloneConfig] = useState(false);
  const [modelTitle, setModelTitle] = useState(p('createEventCategory'));
  const [allInputs, setAllInputs] = useState([]);
  const handleEditPressed = (category, mode) => {
    setModelTitle(
      mode === 'clone' ? p('cloneEventCategory') : p('editEventCategory')
    );
    setSelectedCategory(category);
    setIsEditConfig(mode === 'edit');
    setIsCloneConfig(mode === 'clone');
    setModalVisible(true);
  };

  const handleCloseModal = () => {
    setSelectedCategory(null);
    setModalVisible(false);
  };
  const handleSubmit = async (values) => {
    const payload = {
      maxAccountAttendees: Number(values.maxPerAccount) || 0,
      maxAttendees: Number(values.maxParticipants),
      title: values.title,
      shouldCompanyUpdate: isCloneConfig,
    };

    const isUpdate = !!selectedCategory && !isCloneConfig;
    setLoading(true);
    try {
      if (isCloneConfig) {
        await Api.eventCategories.clone(selectedCategory.id, payload);
      } else if (isUpdate) {
        await Api.eventCategories.update(selectedCategory.id, payload);
      } else {
        await Api.eventCategories.create(payload);
      }
      if (allInputs.length > 0) {
        await Api.eventCategories.bulkUpdateCompanyConfiguartions(allInputs);
        setAllInputs([]);
      }

      toast.success(
        isUpdate ? p('eventCategoryUpdated') : p('eventCategoryCreated')
      );
      handleCloseModal();
      fetchCategories();
    } catch (err) {
      setLoading(false);
      toast.error(
        isUpdate ? p('couldnotUpdateCategory') : p('couldnotCreateCategory')
      );
    }
  };
  const handleRemoveCategory = async (category) => {
    setLoading(true);
    try {
      await Api.eventCategories.delete(category.id);
      toast.success(p('eventCategoryRemoved'));
      handleModal();
      fetchCategories();
    } catch (err) {
      setLoading(false);
      toast.error(p('couldnotRemoveCategory'));
    }
  };
  return (
    <div>
      <DeleteModal
        visible={visible}
        handleModal={handleModal}
        type={p('eventCategory')}
        modalAction={() => handleRemoveCategory(deleteId)}
      />
      <ShareModal
        visible={modalVisible}
        title={modelTitle}
        handleModal={handleCloseModal}
      >
        <AddCategoryForm
          onSubmit={handleSubmit}
          selectedCategory={selectedCategory}
          isEditConfig={isEditConfig}
          isCloneConfig={isCloneConfig}
          allInputs={allInputs}
          setAllInputs={setAllInputs}
        />
      </ShareModal>
      <FormCard
        title={ep('eventCategories')}
        actions={
          <div className="d-flex align-items-center" style={{ gap: 8 }}>
            <SquareIconButton
              icon={<DownloadOutlineIcon />}
              title={`${p('exportCompanyCategories')} (CSV)`}
              id="export-event-categories"
              onClick={exportEventCategoriesForAllCompanies}
            />
            <Button
              size="sm"
              color="primary"
              className="mb-0"
              onClick={() => {
                setModelTitle(p('createEventCategory'));
                setModalVisible(true);
                setIsEditConfig(false);
                setIsCloneConfig(false);
              }}
            >
              {s('create')}
            </Button>
          </div>
        }
        bodyClassName="form-card__body--flush"
      >
        <Table responsive striped className="table--roomy table--stack">
          <thead>
            <tr>
              <th>{s('name')}</th>
              <th>{p('maxParticipants')}</th>
              <th>{p('perCompany')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loadingCategories && skeletonTableLoader(3, 4, 100)}
            {!loadingCategories && categories.length === 0 && (
              <tr>
                <td colSpan={4} className="text-muted text-center">
                  {p('noCategories')}
                </td>
              </tr>
            )}
            {!loadingCategories &&
              categories.map((category) => {
                const companyCount = category.companyConfigs?.length || 0;
                return (
                  <tr key={category.id}>
                    <td className="table-stack__primary">
                      {category.title}
                      <small className="text-muted d-block">
                        {p('companyCount', { count: companyCount })}
                      </small>
                    </td>
                    <td data-label={p('maxParticipants')}>
                      {category.maxAttendees}
                    </td>
                    {/* The per-company default can no longer be set in the
                        form, so 0 means "not set" rather than "no seats". */}
                    <td data-label={p('perCompany')}>
                      {category.maxAccountAttendees || '-'}
                    </td>
                    <td className="table-stack__actions">
                      <div className="icon-actions">
                        <SquareIconButton
                          icon={<EditOutlineIcon />}
                          title={s('edit')}
                          id={`edit-${category.id}`}
                          onClick={() => handleEditPressed(category, 'edit')}
                        />
                        <SquareIconButton
                          icon={<ContentCopyIcon />}
                          title={s('clone')}
                          id={`clone-${category.id}`}
                          onClick={() => handleEditPressed(category, 'clone')}
                        />
                        <SquareIconButton
                          icon={<DeleteOutlineIcon />}
                          title={s('delete')}
                          tone="danger"
                          id={`delete-${category.id}`}
                          onClick={() => handleModal(category)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </Table>
      </FormCard>
    </div>
  );
};

export default EventCategoryCard;
